import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAssociateAnalytics,
  getAssociateAnalyticsSeries,
  getAssociateFeedbacks,
  getAssociateProfile,
  getKycStatus,
  submitKyc,
  updateAssociateProfile,
  type SubmitKycPayload,
  type UpdateAssociateProfilePayload,
} from '../api/associates';
import type { AssociateFeedbacksQuery } from '../types/api';
import { getTokenStore } from '../api/token-store';

export const ASSOCIATE_PROFILE_KEY = ['associate', 'profile'] as const;
export const ASSOCIATE_KYC_KEY = ['associate', 'kyc'] as const;
export const ASSOCIATE_ANALYTICS_KEY = ['associate', 'analytics'] as const;
export const ASSOCIATE_FEEDBACKS_KEY = ['associate', 'feedbacks'] as const;

export function useAssociateProfile() {
  return useQuery({
    queryKey: ASSOCIATE_PROFILE_KEY,
    queryFn: getAssociateProfile,
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 60_000,
  });
}

export function useUpdateAssociateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAssociateProfilePayload) => updateAssociateProfile(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ASSOCIATE_PROFILE_KEY }),
  });
}

export function useKycStatus() {
  return useQuery({
    queryKey: ASSOCIATE_KYC_KEY,
    queryFn: getKycStatus,
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 30_000,
  });
}

export function useSubmitKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubmitKycPayload) => submitKyc(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ASSOCIATE_KYC_KEY });
      qc.invalidateQueries({ queryKey: ASSOCIATE_PROFILE_KEY });
    },
  });
}

export function useAssociateAnalytics() {
  return useQuery({
    queryKey: ASSOCIATE_ANALYTICS_KEY,
    queryFn: getAssociateAnalytics,
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 30_000,
  });
}

export function useAssociateAnalyticsSeries(year: number) {
  return useQuery({
    queryKey: ['associate', 'analytics', 'series', year],
    queryFn: () => getAssociateAnalyticsSeries(year),
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 5 * 60_000,
  });
}

export function useAssociateFeedbacks(query?: AssociateFeedbacksQuery) {
  return useQuery({
    queryKey: [...ASSOCIATE_FEEDBACKS_KEY, query],
    queryFn: () => getAssociateFeedbacks(query),
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
